<template>
  <div class="strategy-modal">
    <div ref="StrategyImplementationModal" class="modal fade" data-bs-backdrop="static" id="strategyImplementationModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-wide" v-draggable-modal>
        <div class="modal-content bg-primary">

          <!-- Header -->
          <div class="modal-header">
              <div class="h5 modal-title text-light me-2"> {{ $t("views.strategy.map.modalHeader") }} </div>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <!-- Body -->
          <div class="modal-body bg-light">

            <!-- Heading -->
            <div class="row">
              <div class="col h4 foc-fw-600"> {{ selectedStrategy.name }} </div>
            </div>
            <div class="d-flex justify-content-between align-items-center fs-08 mb-4">
              <div>
                {{ getYear(selectedStrategy.validFrom) }} – {{ getYear(selectedStrategy.validTo) }}
                <span v-if="selectedStrategy.responsible?.name"> | {{ selectedStrategy.responsible.name }} </span>
              </div>

              <!-- Switch 'only open objects' -->
              <div class="form-check form-switch fs-09">
                <input v-model="switchConversion" type="checkbox" role="switch" id="switchConversion" class="form-check-input">
                <label class="switchConversion" for="switchConversion"> {{ $t("views.strategy.map.modalSwitchConversion") }} </label>
              </div>
            </div>

            <!-- Implementation objects table-->
            <div v-if="implementationObjects?.length">
              <table class="table table-sm">
                  <thead class="table-light fs-09">
                    <tr class="">
                      <th scope="col">{{ $t("views.strategy.map.modalImplObject") }}</th>
                      <th scope="col">{{ $t("views.strategy.map.modalImplObjectStatus") }}</th>
                      <th scope="col">{{ $t("views.strategy.map.modalImplObjectResponsible") }}</th>
                      <th scope="col">{{ $t("views.strategy.map.modalImplObjectStart") }}</th>
                      <th
                        scope="col">{{ $t("views.strategy.map.modalImplObjectEnd") }}
                        <span class=" text-primary ms-1 fs-07">▲</span>
                      </th>
                      <th scope="col">Time %</th>
                    </tr>
                  </thead>

                  <tbody class="table-light fs-09">
                    <tr v-for="(obj, i) in filteredImplementationObjects" :key="i">
                      <td v-if="obj.type === 'objective'">
                        <div class="cont-icon-org-unit d-inline-block me-1 cursor-help">
                          <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconObjective'"> <title>{{ $t("views.relationships.strategyObjective") }}</title> <use xlink:href="@/assets/icons/icons.svg#objective" v-tooltip="'tooltip.iconObjective'"/> </svg> &nbsp;
                          <a :href="createLink('objective', obj.id)" target="_blank"> {{ obj.name }} </a>
                        </div>
                      </td>
                      <td v-if="obj.type === 'project'">
                        <div class="cont-icon-org-unit d-inline-block me-1 cursor-help">
                          <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconProject'"> <title>{{ $t("views.relationships.strategyProject") }}</title> <use xlink:href="@/assets/icons/icons.svg#project" v-tooltip="'tooltip.iconProject'"/> </svg> &nbsp;
                          <a :href="createLink('project', obj.id)" target="_blank"> {{ obj.name }} </a>
                        </div>
                      </td>
                      <td v-if="obj.type === 'measure'">
                        <div class="cont-icon-org-unit d-inline-block me-1 cursor-help">
                          <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconMeasure'"> <title>{{ $t("views.relationships.strategyMeasure") }}</title> <use xlink:href="@/assets/icons/icons.svg#measure" v-tooltip="'tooltip.iconMeasure'"/> </svg> &nbsp;
                        </div>
                        {{ obj.name }}
                      </td>

                      <td>
                        <div v-if="obj.type === 'objective' || obj.type === 'project'" >
                          <div class="progress" style="height: 8px;">
                            <div class="progress-bar bg-secondary" :class=getStatusColor(obj.prediction) role="progressbar" :style="{ width: 25 * obj.conversion + '%' }"  ></div>
                          </div>
                          <div class="small text-muted mt-0 fs-07"> {{ 25 * obj.conversion }}% </div>
                        </div>
                        <div v-if="obj.type === 'measure'" >
                          <div class="progress" style="height: 8px;">
                            <div class="progress-bar bg-secondary" role="progressbar" :style="{ width: 25 * obj.conversion + '%' }"  ></div>
                          </div>
                          <div class="small text-muted mt-0 fs-07"> {{ 25 * obj.conversion }}% </div>
                        </div>
                      </td>

                      <td>
                        <span v-if="obj.type === 'measure'">{{ obj.responsible }}</span>
                        <span v-if="obj.type === 'objective'">{{ obj.responsible }}</span>
                        <span v-if="obj.type === 'project'">{{ obj.responsible }}</span>
                        <span v-if="obj.type === 'kpi'">{{ obj.responsible }}</span>
                      </td>

                      <td>{{ formatDate(new Date(obj.validFrom)) }}</td>
                      <td :class="new Date(obj.validTo) < new Date() ? 'text-danger' : 'text-dark'">{{ formatDate(new Date(obj.validTo)) }}</td>

                      <td style="width: 60px;">
                        <div class="progress" style="height: 8px;">
                          <div class="progress-bar bg-primary" role="progressbar" :style="{ width: timeProgress(obj) + '%' }"  ></div> <!-- FARBIG :class="progressColor(timeProgress(obj))" -->
                        </div>
                        <div class="small text-muted mt-0 fs-07"> {{ timeProgress(obj) }}% </div>
                      </td>

                    </tr>
                  </tbody>

              </table>
            </div>
            <div v-else class="mt-4 text-center bg-secondary text-white">{{ $t("views.strategy.map.modalImplObjectsNoData") }}</div>
          </div>

          <!-- Footer -->
          <div class="modal-footer bg-light">
              <button type="button" class="btn btn-sm btn-outline-secondary me-2" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as formatUtils from '@/composables/formatUtils'

export default {
  name: 'StrategyImplementationModal',
  data () {
    return {
      switchConversion: false
    }
  },

  props: {
    strategy: {
      type: Object,
      required: true
    }
  },

  computed: {
    selectedStrategy () {
      return this.$store.state.selectedStrategy
    },

    filteredImplementationObjects () {
      let n = '4'
      if (this.switchConversion) n = this.switchConversion.checked ? '4' : '3'
      if (this.implementationObjects) {
        return this.implementationObjects.filter(obj => {
          return obj.conversion <= n
        })
      } else {
        return []
      }
    },

    implementationObjects () {
      if (!this.selectedStrategy) return []

      // projects
      const projects = this.selectedStrategy.relationships
        ?.filter(r => r.type === 'project')
        .map(r => ({
          id: r.projectUuid,
          type: r.type,
          name: r.name,
          validFrom: r.validFrom,
          validTo: r.validTo,
          responsible: r.responsible || null,
          conversion: r.conversion || null,
          prediction: r.prediction || null
        })) || []

      // objectives
      const objectives = this.selectedStrategy.relationships
        ?.filter(r => r.type === 'objective')
        .map(r => ({
          id: r.objectiveUuid,
          type: r.type,
          name: r.name,
          validFrom: r.validFrom,
          validTo: r.validTo,
          responsible: r.responsible || null,
          conversion: r.conversion || null,
          prediction: r.prediction || null
        })) || []

      // measures
      const measures = this.selectedStrategy.measures
        ?.map(m => ({
          id: m.id,
          type: 'measure',
          name: m.title,
          validFrom: m.validFrom,
          validTo: m.validTo,
          responsible: m.responsible.name || null,
          conversion: m.conversion || null,
          prediction: null
        })) || []

      // Zusammenführen & sortieren
      return [...projects, ...objectives, ...measures].sort(
        (a, b) => new Date(a.validTo) - new Date(b.validTo)
      )
    }
  },

  methods: {
    getYear (date) {
      return new Date(date).getFullYear()
    },
    formatDate (date) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.formatDate(userLocale, date)
    },
    createLink (type, uuid) {
      let url = ''
      if (type === 'objective') url = '/objectives/manage/objectives?id=' + uuid
      if (type === 'project') url = '/projects/manage/projects?id=' + uuid
      if (type === 'kpi') url = '/monitoring/manage/kpis?id=' + uuid
      if (type === 'strategy') url = '/strategies/manage/strategies?id=' + uuid
      return url
    },
    getHarveyColor (prediction) {
      return prediction === 'Y' ? '#198754' : prediction === 'D' ? '#ebad20' : '#dc3545'
    },
    getPredictionColor (prediction) {
      return prediction === 'Y' ? 'bg-success-subtle' : prediction === 'D' ? 'bg-warning-subtle' : 'bg-danger-subtle'
    },
    getStatusColor (prediction) {
      if (prediction === 'Y') return 'bg-success'
      if (prediction === 'D') return 'bg-warning'
      return 'bg-danger'
    },
    timeProgress (obj) {
      const start = new Date(obj.validFrom)
      const end = new Date(obj.validTo)
      const now = new Date()

      if (now < start) return 0
      if (now > end) return 100

      const total = end - start
      const elapsed = now - start

      return Math.round((elapsed / total) * 100)
    },
    progressColor (p) {
      if (p < 50) return 'bg-success'
      if (p < 80) return 'bg-warning'
      return 'bg-danger'
    }
  }
}
</script>

<style scoped>
.modal-wide {
  width: 1000px !important;
  max-width: none !important;
}
</style>
